import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, switchMap, tap, throwError } from 'rxjs';

import { GoogleInitOptions, GoogleTokenResponse, GoogleUserResponse, SocialUser } from '@personal-area/global/social-authorization/models';

import { BaseLoginProvider } from './base-login-provider';

declare let google: {
  accounts: {
    oauth2: {
      initTokenClient(opts: GoogleInitOptions): GoogleClient
    }
  }
};

export interface GoogleClient {
  requestAccessToken(): () => void
}

export class GoogleLoginProvider extends BaseLoginProvider {
  public static readonly PROVIDER_ID: string = 'GOOGLE';
  public static readonly DISCOVERY_DOC: string = 'https://accounts.google.com/.well-known/openid-configuration';
  private _onResponse: Subject<SocialUser> = new Subject<SocialUser>();
  private _client: GoogleClient | undefined;

  constructor(
    private _clientId: string,
    private _http: HttpClient,
  ) {
    super();
  }

  public initialize(): Observable<void> {
    if (!this._clientId) {
      return throwError(() => new Error('App id for google auth is not found'));
    }

    return this._loadScript(
      GoogleLoginProvider.PROVIDER_ID,
      'https://accounts.google.com/gsi/client'
    ).pipe(
      tap(() => {
        this._client = google.accounts.oauth2.initTokenClient({
          client_id: this._clientId,
          scope: 'openid email profile',
          callback: (response: GoogleTokenResponse) => {
            const headers = new HttpHeaders({ Authorization: 'Bearer ' + response.access_token });

            this._http.get<{ userinfo_endpoint: string }>(GoogleLoginProvider.DISCOVERY_DOC)
              .pipe(
                switchMap(doc => this._http.get<GoogleUserResponse>(doc['userinfo_endpoint'], { headers }))
              ).subscribe(user => {
              this._onResponse.next({
                provider: GoogleLoginProvider.PROVIDER_ID,
                id: user.sub,
                email: user.email,
                name: user.name,
                photoUrl: user.picture,
                firstName: user.given_name,
                lastName: user.family_name,
                authToken: response.access_token
              });
            });
          }
        });
      })
    );
  }

  public signIn(): Observable<SocialUser> {
    this._client?.requestAccessToken();
    return this._onResponse.asObservable();
  }
}
